import { useTranslator } from '@Localization';
import { ChartOptions } from 'chart.js';
import { ReactElement } from 'react';
import { Line } from 'react-chartjs-2';
import { IChartPoint } from '../../../jobMetrics.model';
import { JOB_STATUS } from '../../../jobs.model';

interface IProps {
  loss: IChartPoint[];
  validationAccuracy: IChartPoint[];
  trainingAccuracy: IChartPoint[];
  status: JOB_STATUS;
}

export const LossMetrics = ({ loss, validationAccuracy, trainingAccuracy, status }: IProps): ReactElement => {
  const { t } = useTranslator();
  const roundDecimal = (
    points: IChartPoint[]
  ): {
    x: string;
    y: string;
  }[] => points.map(({ x, y }) => ({ x: x.toFixed(3), y: y.toFixed(3) }));

  const options: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        min: 0,
        title: {
          font: { size: 18 },
          display: true,
          text: t('loss.chart.xAxis'),
        },
        ticks: {
          stepSize: 5,
        },
      },
      yAxis1: {
        type: 'linear',
        title: {
          font: { size: 18 },
          display: true,
          text: t('loss.chart.yAxis1'),
        },
        min: 0,

        position: 'left',
      },
      yAxis2: {
        type: 'linear',
        title: {
          font: { size: 18 },
          display: true,
          text: t('loss.chart.yAxis2'),
        },
        min: 0,
        ticks: {
          stepSize: 1,
        },
        grid: {
          drawOnChartArea: false,
        },
        position: 'right',
      },
    },
  };

  const data = {
    datasets: [
      {
        label: t('loss.chart.loss'),
        data: roundDecimal(loss),
        fill: false,
        borderColor: '#b70000',
        smoothed: true,
        steppedLine: false,
        borderWidth: 2,
        yAxisID: 'yAxis1',
        clip: 5,
      },
    ],
  };

  if (validationAccuracy.length > 0) {
    data.datasets.push({
      label: t('loss.chart.validation_accuracy'),
      data: roundDecimal(validationAccuracy),
      fill: false,
      borderColor: '#e9a255',
      smoothed: false,
      steppedLine: true,
      borderWidth: 2,
      yAxisID: 'yAxis2',
      clip: 5,
    });

    if (trainingAccuracy.length > 0) {
      data.datasets.push({
        label: t('loss.chart.training_accuracy'),
        data: roundDecimal(trainingAccuracy),
        fill: false,
        borderColor: '#4859b5',
        smoothed: false,
        steppedLine: true,
        borderWidth: 2,
        yAxisID: 'yAxis2',
        clip: 5,
      });
    }
  }

  return (
    <div className='loss-metrics'>
      <h4>{t('loss.title')}</h4>
      {![JOB_STATUS.FINISHED, JOB_STATUS.IMPORTED].includes(status) && <p>{t('loss.available_real_time')}</p>}
      <div className='chart-container'>
        <Line
          data={data}
          options={options}
        />
      </div>
    </div>
  );
};
